// Web Push subscription helper — registers /sw.js and subscribes to push
// with the server's VAPID public key. Safe to call multiple times.
import { api } from "./api";

const urlBase64ToUint8Array = (b64) => {
    const padding = "=".repeat((4 - (b64.length % 4)) % 4);
    const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
};

export const isPushSupported = () =>
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

export const getPushPermission = () =>
    typeof Notification !== "undefined" ? Notification.permission : "unsupported";

/**
 * Registers /sw.js, asks the user for permission if needed, subscribes
 * with VAPID, and posts the subscription to the backend.
 * Returns {ok, reason} — reason describes any failure so the UI can help.
 */
export const enablePushNotifications = async () => {
    if (!isPushSupported()) return { ok: false, reason: "unsupported" };

    try {
        const reg = await navigator.serviceWorker.register("/sw.js");
        if (Notification.permission === "default") {
            const perm = await Notification.requestPermission();
            if (perm !== "granted") return { ok: false, reason: perm };
        } else if (Notification.permission === "denied") {
            return { ok: false, reason: "denied" };
        }

        const { data } = await api.get("/alerts/vapid-public-key");
        const publicKey = data?.public_key;
        if (!publicKey) return { ok: false, reason: "no_vapid_key" };

        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
            sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey),
            });
        }

        const raw = sub.toJSON();
        await api.post("/alerts/push/subscribe", {
            endpoint: raw.endpoint,
            keys: raw.keys,
        });
        return { ok: true };
    } catch (e) {
        console.error("push enable failed", e);
        return { ok: false, reason: "error" };
    }
};

export const sendTestPush = async () => {
    const r = await api.post("/alerts/push/test");
    return r.data;
};
